import { useCallback } from 'react';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import Bugsnag from '@bugsnag/js';

import { IValues } from '../../utils/common/types';
import { allowMoreResults, increaseNumberOfResults } from '../common/localStorage';
import analytics from '../../services/analytics';
import { getCompletions } from '../common/completions';
import { getCategoryName } from '../common/category';
import useStore from '../../services/store';

type UseGenerateMoreResultsArgs = {
  category: number;
  formProps: FormikProps<IValues>;
};

function useGenerateMoreResults({ category, formProps }: UseGenerateMoreResultsArgs) {
  const { t } = useTranslation();
  const setMoreResultsAreGenerating = useStore((state) => state.setMoreResultsAreGenerating);
  const showGenericError = useStore((state) => state.showGenericError);
  const addResults = useStore((state) => state.addResults);

  const generateMoreResults = useCallback(async () => {
    if (!allowMoreResults()) {
      showGenericError(t('editor.resultsExceededAlert'));
      return;
    }

    try {
      setMoreResultsAreGenerating(true);

      const { actualResults, textGenerated, bodyParams, tokenInformation } = await getCompletions({
        values: formProps.values,
        selectedCategory: category,
        generateMore: true,
      });

      analytics.generateMore({
        page_path: window.location.pathname,
        category_name: getCategoryName(category),
        text_information: bodyParams,
      });
      analytics.textGenerated({
        page_path: window.location.pathname,
        text_information: textGenerated,
        category_name: getCategoryName(category),
        token_information: tokenInformation,
      });
      addResults({
        category,
        newResults: actualResults.map((result) => ({ ...result, category })),
      });
      increaseNumberOfResults();
      showGenericError('');
    } catch (err) {
      const errMessage = err?.response?.data?.message || err.message;

      Bugsnag.notify(`Generate more results error: ${errMessage}`);
      console.error(err);
      showGenericError(errMessage);
    }
    setMoreResultsAreGenerating(false);
  }, [formProps.values, category, t]);

  return generateMoreResults;
}
export default useGenerateMoreResults;
